import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Upload, Loader2, CheckCircle2, ArrowRight, ArrowLeft, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { useSiteCopy } from "@/lib/copy";
import PhotoPicker from "@/components/PhotoPicker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const PROJECT_TYPES = [
  "New Pool Installation",
  "Renovation / Resurfacing",
  "Repair (Pump, Filter, Heater)",
  "Weekly Maintenance",
  "Pool Opening",
  "Pool Closing / Winterization",
  "Other",
];

const POOL_TYPES = ["In-ground concrete", "In-ground vinyl", "Fiberglass", "Above-ground", "Spa / Hot tub", "Not sure yet"];

const PHOTO_GUIDE = [
  { title: "Wide pool shot", text: "Step back so the entire pool, decking and surroundings fit in one frame.", emoji: "🏊" },
  { title: "Equipment pad", text: "Show pump, filter, heater and any plumbing on the equipment pad.", emoji: "⚙️" },
  { title: "Decking & surface", text: "Capture cracks, plaster fading, tile damage, or coping issues up close.", emoji: "🧱" },
  { title: "Yard access", text: "A photo from the street/driveway showing access for trucks or equipment.", emoji: "🚪" },
  { title: "Anything unusual", text: "Custom features, automation panels, lights — anything you want us to see.", emoji: "✨" },
];

export default function QuotePage() {
  const navigate = useNavigate();
  const t = useSiteCopy();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photos, setPhotos] = useState([]); // [{id, url, name}]
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "",
    project_type: "", pool_type: "", description: "",
    netvendor_ref: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        // Don't set Content-Type explicitly — axios + FormData auto-generates the multipart boundary.
        const r = await api.post("/upload", fd);
        setPhotos((p) => [...p, { id: r.data.id, url: fileUrl(r.data.id), name: file.name }]);
      }
      toast.success("Photos uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (id) => setPhotos((p) => p.filter((x) => x.id !== id));

  const submit = async () => {
    if (!form.name || !form.email || !form.phone || !form.address || !form.project_type || !form.description) {
      toast.error("Please fill out all required fields"); setStep(0); return;
    }
    setSubmitting(true);
    try {
      await api.post("/quotes", { ...form, photo_ids: photos.map((p) => p.id) });
      toast.success("Quote sent! We'll be in touch shortly.");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit quote");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 md:px-8 md:py-16">
      <div className="max-w-2xl">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">{t("quote.eyebrow", "Free quote")}</span>
        <h1 className="mt-2 font-display text-4xl font-bold text-slate-900 md:text-5xl wavy">{t("quote.title", "Tell us about your pool.")}</h1>
        <p className="mt-3 text-slate-600">{t("quote.subtitle", "Three steps. No phone tag. We'll respond same-day.")}</p>
      </div>

      {/* Stepper */}
      <div className="mt-8 flex items-center gap-3 text-sm">
        {["Project", "Photos", "Review"].map((label, i) => (
          <div key={label} className="flex items-center gap-3">
            <div className={`grid h-8 w-8 place-items-center rounded-full font-semibold ${i <= step ? "bg-cyan-600 text-white" : "bg-slate-100 text-slate-500"}`} data-testid={`quote-step-${i}-indicator`}>
              {i + 1}
            </div>
            <span className={i === step ? "font-medium text-slate-900" : "text-slate-500"}>{label}</span>
            {i < 2 && <div className="h-px w-10 bg-slate-200 sm:w-16" />}
          </div>
        ))}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-12">
        <div className="rounded-2xl border border-cyan-100 bg-white p-6 shadow-sm md:p-8 lg:col-span-7">
          {step === 0 && (
            <div className="space-y-5">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="name">Full name *</Label>
                  <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1" data-testid="quote-name-input" />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1" data-testid="quote-email-input" />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1" data-testid="quote-phone-input" />
                </div>
                <div>
                  <Label htmlFor="address">Service address *</Label>
                  <Input id="address" value={form.address} onChange={(e) => update("address", e.target.value)} className="mt-1" data-testid="quote-address-input" />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Project type *</Label>
                  <Select value={form.project_type} onValueChange={(v) => update("project_type", v)}>
                    <SelectTrigger className="mt-1" data-testid="quote-project-type-select"><SelectValue placeholder="Choose one…" /></SelectTrigger>
                    <SelectContent>
                      {PROJECT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pool type</Label>
                  <Select value={form.pool_type} onValueChange={(v) => update("pool_type", v)}>
                    <SelectTrigger className="mt-1" data-testid="quote-pool-type-select"><SelectValue placeholder="If applicable…" /></SelectTrigger>
                    <SelectContent>
                      {POOL_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label htmlFor="desc">Tell us about the project *</Label>
                <Textarea id="desc" rows={5} value={form.description} onChange={(e) => update("description", e.target.value)} className="mt-1" placeholder="What are you hoping to do? Timeline, dimensions if known, anything to be aware of." data-testid="quote-description-textarea" />
              </div>
              <div>
                <Label htmlFor="nv-ref">
                  Property manager NetVendor ID <span className="text-slate-400">(optional)</span>
                </Label>
                <Input
                  id="nv-ref"
                  value={form.netvendor_ref}
                  onChange={(e) => update("netvendor_ref", e.target.value)}
                  className="mt-1"
                  placeholder="e.g. NV-4427 — if your property manager uses NetVendor"
                  data-testid="quote-netvendor-ref-input"
                />
                <p className="mt-1 text-[11px] text-slate-500">If you're requesting this on behalf of an HOA or apartment building that vets contractors through NetVendor, paste their code so we can tag this quote to their account.</p>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setStep(1)} className="btn-aqua" data-testid="quote-step-0-next-button">Continue <ArrowRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="rounded-xl border-2 border-dashed border-cyan-300 bg-cyan-50/40 p-8 text-center">
                <ImageIcon className="mx-auto h-8 w-8 text-cyan-600" />
                <p className="mt-3 font-display text-lg text-slate-900">Upload 3–10 photos</p>
                <p className="mx-auto mt-1 max-w-sm text-sm text-slate-600">JPG, PNG or HEIC, up to 15MB each. Follow the capture guide on the right.</p>
                <div className="mt-4 flex justify-center">
                  <PhotoPicker onFiles={onPickFiles} busy={uploading} testIdPrefix="quote-photo" />
                </div>
              </div>

              {photos.length > 0 && (
                <div className="mt-5 grid grid-cols-3 gap-3 md:grid-cols-4">
                  {photos.map((p) => (
                    <div key={p.id} className="group relative overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                      <img src={p.url} alt={p.name} className="h-28 w-full object-cover" />
                      <button onClick={() => removePhoto(p.id)} className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white opacity-0 transition group-hover:opacity-100" data-testid={`quote-remove-photo-${p.id}`}><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={() => setStep(0)} data-testid="quote-step-1-back-button"><ArrowLeft className="h-4 w-4" /> Back</Button>
                <Button onClick={() => setStep(2)} className="btn-aqua" data-testid="quote-step-1-next-button">Review <ArrowRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h3 className="font-display text-xl font-bold">Looks good?</h3>
              <dl className="mt-4 grid gap-3 text-sm">
                {[
                  ["Name", form.name], ["Email", form.email], ["Phone", form.phone],
                  ["Address", form.address], ["Project", form.project_type],
                  ["Pool type", form.pool_type || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100 pb-2">
                    <dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-900 text-right max-w-[60%]">{v || "—"}</dd>
                  </div>
                ))}
                <div className="border-b border-slate-100 pb-2">
                  <dt className="text-slate-500">Description</dt>
                  <dd className="mt-1 whitespace-pre-wrap text-slate-800">{form.description}</dd>
                </div>
                <div>
                  <dt className="text-slate-500">Photos ({photos.length})</dt>
                  <dd className="mt-2 flex flex-wrap gap-2">
                    {photos.map((p) => <img key={p.id} src={p.url} alt="" className="h-14 w-14 rounded object-cover" />)}
                  </dd>
                </div>
              </dl>
              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)} data-testid="quote-step-2-back-button"><ArrowLeft className="h-4 w-4" /> Back</Button>
                <Button onClick={submit} disabled={submitting} className="btn-aqua" data-testid="quote-submit-button">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {submitting ? "Sending…" : "Send my quote request"}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Capture guide */}
        <aside className="lg:col-span-5">
          <div className="sticky top-24 rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50 to-white p-6 md:p-7">
            <div className="flex items-center gap-2 text-cyan-700">
              <Camera className="h-5 w-5" />
              <span className="text-xs font-semibold uppercase tracking-widest">Capture guide</span>
            </div>
            <h3 className="mt-2 font-display text-xl font-bold text-slate-900">{t("quote.guide_title", "How to photograph the project")}</h3>
            <p className="mt-1 text-sm text-slate-600">{t("quote.guide_subtitle", "A quick checklist so we can quote accurately the first time.")}</p>
            <ul className="mt-5 space-y-3">
              {PHOTO_GUIDE.map((g, i) => (
                <li key={g.title} className="flex gap-3 rounded-xl border border-cyan-100 bg-white p-3" data-testid={`quote-photo-guide-${i}`}>
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-cyan-100 text-lg">{g.emoji}</div>
                  <div>
                    <div className="font-medium text-slate-900">{g.title}</div>
                    <div className="text-sm text-slate-600">{g.text}</div>
                  </div>
                </li>
              ))}
            </ul>
            <p className="mt-5 text-xs text-slate-500">{t("quote.guide_tip", "Tip: shoot in daylight when possible — water and surfaces read more accurately.")}</p>
          </div>
        </aside>
      </div>
    </div>
  );
}
